import axios from "axios";
import moment from "moment";
import _, { filter } from "lodash";

import { MagellanManualEditConfiguration } from "pages/Forcasting/types";
import {
  PATHS,
  REACT_APP_PROD_API_RIDGEWOOD,
  REACT_APP_API_LOGIN,
  REACT_APP_DEV_RESTOCK,
  REACT_APP_PROD_API,
  REACT_APP_SUBSCRIPTION,
  DEMO_API,
  DASHBOARD_DEMO_DATA_TOKEN,
} from "constants/constants";
import { getAccessToken } from "../utils/utils";
import {
  ApolloShipmentDisputeApi,
  CatalogTrackerData,
  ForgotPasswordProps,
  IPnlEdit,
  IPnlEditItem,
  ISnapshot,
  ISnapshotApi,
  ISignificantSalesChangesParams,
  ISummaryChartData,
  ISummaryChartParams,
  KeplerDataResponse,
  ListingHealthAPIResponse,
  PostCuriosityFeedback,
  PostCuriosityUpvote,
  SalesBreakoutParams,
  SalesReportsProps,
  ShipmentStatusData,
  SignificantSalesChangesChartResponse,
  SignificantSalesChangesTableResponse,
  UserHasSellerOrNotResponseParams,
  UserSignUP,
  CuriosityUpvoteProps,
  IPNLApi,
  AccountingTableDataResponse,
  IRankChangesSave,
  IRankChangesSaveApi,
  UserModifyData,
} from "api/types";

axios.defaults.headers.common = { Authorization: `Bearer ${getAccessToken()}` };

const getReplenishmentData = async (
  seller_id?: string[],
  orders_list?: string[],
  pageSize?: number,
  filters?: string[],
  page?: number
) => {
  try {
    const accessToken = getAccessToken();
    const dataToSend: any = {
      page,
      per_page: pageSize,
    };
    if (filters && filters.length && filters[1].length) {
      let arr = filters[1]
        .toString()
        .split(",")
        .map((item: string) => item.trim())
        .map((value) => {
          if (!isNaN(Number(value))) return Number(value);
          else return value;
        });
      dataToSend["filters_dict"] = { [filters[0]]: arr };
    }
    if (
      orders_list &&
      orders_list.length &&
      orders_list[0] !== "" &&
      orders_list[1] !== ""
    ) {
      dataToSend["orders_list"] = [orders_list];
    }

    const response = await axios.post<KeplerDataResponse>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.GET_REPLENISHMENT_DATA_DEV,
      {
        ...dataToSend,
      },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

const getSellerLimitsData = async () => {
  try {
    const accessToken = getAccessToken();

    const { data } = await axios.get<any>(
      REACT_APP_DEV_RESTOCK + PATHS.GET_SELLER_LIMITS_DATA,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const saveReplenishmentData = async (manualConfigurationData: any[]) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.put<any[]>(
      REACT_APP_DEV_RESTOCK + PATHS.SAVE_REPLENISHMENT_DATA,
      { data: manualConfigurationData },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getReplenishmentChartData = async (filters?: string) => {
  try {
    const accessToken = getAccessToken();
    const params = {
      seller_ids: filters,
    };
    if (process.env.NODE_ENV === "development") {
      //@ts-ignore
      params["time_interval"] = 30;
    }
    const { data } = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.REPLENISHMENT_CHART,
      {
        headers: {
          Authorization: accessToken,
        },
        params,
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getListingHealthChartData = async (filters?: string) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.LISTING_HEALTH_CHART,

      {
        headers: {
          Authorization: accessToken,
        },
        params: {
          seller_ids: filters,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getShipmentStatus = async (number_of_days: string, seller_id: string) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<ShipmentStatusData>(
      REACT_APP_PROD_API_RIDGEWOOD +
        PATHS.SHIPMENT_STATUS(number_of_days, seller_id),
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const updateApolloShipmentDisputeState = async (
  shipmentId: string,
  State: number
): Promise<ApolloShipmentDisputeApi> => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<ApolloShipmentDisputeApi>(
      REACT_APP_DEV_RESTOCK + PATHS.CREATE_APOLLO_SHIPMENT_DISPUTE(shipmentId),
      null,
      {
        headers: {
          Authorization: accessToken,
        },
        params: {
          State,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getSalesReportsData = async () => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<Promise<SalesReportsProps[]>>(
      REACT_APP_DEV_RESTOCK + PATHS.SALES_REPORTS,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getPromotionCalendarData = async (
  aggregationType: string,
  timeFrames: number,
  sellerId: string,
  asin: string = ""
) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<Promise<any>>(
      REACT_APP_PROD_API +
        PATHS.GET_PROMOTION_CALENDAR_DATA(
          aggregationType,
          timeFrames,
          sellerId,
          asin
        ),
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getPromotionCalendarTableData = async (
  orders_list?: string[],
  pageSize?: number,
  filters?: string[],
  page?: number,
  sellerIds?: string[]
) => {
  try {
    const accessToken = getAccessToken();
    const dataToSend: any = {
      page,
      per_page: pageSize,
    };
    if (sellerIds && sellerIds.length) {
      dataToSend["filters_dict"] = {
        ["seller_id"]: sellerIds,
      };
    }
    if (filters && filters.length && filters[1].length) {
      const len = filters.length;
      const filterKeys = filters.filter((item: any, id: number) => {
        if (id % 2 !== 0) return id;
      });

      // if (filters[0] === "end_date" || filters[0] === "start_date") {
      //   const outputFormat = "YYYY-MM-DD";
      //   const outputDate = moment(filters[1][0]).format(outputFormat);
      //   console.log(outputDate);
      //   console.log(filters);
      //   console.log(filters[1][0]);
      //   filters[1][0] = outputDate;
      // }

      let arr = filters[1]
        .toString()
        .split(",")
        .map((item: string) => item.trim())
        .map((value) => {
          if (!isNaN(Number(value))) return Number(value);
          else return value;
        });
      dataToSend.filters_dict = {
        ...dataToSend.filters_dict,
        [filters[0]]: arr,
      };

      if (
        _.has(dataToSend.filters_dict, "is_overlapping") &&
        dataToSend.filters_dict.is_overlapping.length > 0
      ) {
        dataToSend.filters_dict = {
          ...dataToSend.filters_dict,
          is_overlapping:
            dataToSend.filters_dict.is_overlapping[0] === "true" ? true : false,
        };
      }
    }
    if (orders_list && orders_list.length && orders_list[0][1]) {
      dataToSend["orders_list"] = [orders_list];
    }
    const { data } = await axios.post<any>(
      REACT_APP_PROD_API + PATHS.GET_PROMOTION_CALENDAR_TABLE_DATA,
      {
        ...dataToSend,
      },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getReplenishmentCalculationStatus = async () => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get(
      REACT_APP_DEV_RESTOCK + PATHS.GET_REPLENISHMENT_CALCULATION_STATUS,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getUserDetails = async (token: string) => {
  try {
    const { data } = await axios.get<any[]>(
      REACT_APP_DEV_RESTOCK + PATHS.GET_USER_DETAILS,
      {
        headers: {
          Authorization: token,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getCompanyAnalytics = async () => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get(
      REACT_APP_PROD_API + PATHS.GET_COMPANY_ANALYTICS,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
}

const getUserAnalytics = async () => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get(
      REACT_APP_PROD_API + PATHS.GET_USER_ANALYTICS,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
}

const getListingHealth = async (
  orders_list?: string[],
  pageSize?: number,
  filters?: string[],
  page?: number,
  sellerIds?: string[]
) => {
  try {
    const accessToken = getAccessToken();
    const dataToSend: any = {
      page,
      per_page: pageSize,
    };
    if (sellerIds && sellerIds.length) {
      dataToSend["filters_dict"] = {
        ["seller_id"]: sellerIds,
      };
    }
    if (filters && filters.length && filters[1].length) {
      const len = filters.length;
      const filterKeys = filters.filter((item: any, id: number) => {
        if (id % 2 !== 0) return id;
      });
      let arr = filters[1]
        .toString()
        .split(",")
        .map((item: string) => item.trim())
        .map((value) => {
          if (!isNaN(Number(value))) return Number(value);
          else return value;
        });

      dataToSend.filters_dict = {
        ...dataToSend.filters_dict,
        [filters[0]]: arr,
      };
    }
    if (orders_list && orders_list.length && orders_list[0][1]) {
      dataToSend["orders_list"] = [orders_list];
    }

    const { data } = await axios.post<ListingHealthAPIResponse>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.LISTING_HEALTH,
      {
        ...dataToSend,
      },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const userLogin = async (username: string, password: string) => {
  try {
    const data = await axios.post<UserSignUP>(
      REACT_APP_API_LOGIN + PATHS.LOGIN,

      {
        username,
        password,
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const userSignup = async (singUpPayload: Object) => {
  try {
    const data = await axios.post<UserSignUP>(
      REACT_APP_API_LOGIN + PATHS.SIGNUP_INIT,
      singUpPayload
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const userSignupVerify = async (
  confirmation_code: string,
  username: string
) => {
  try {
    const data = await axios.post<UserSignUP>(
      REACT_APP_API_LOGIN + PATHS.SIGNUP_CONFIRM,
      {
        confirmation_code,
        username,
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const resendOTP = async (username: string) => {
  try {
    const data = await axios.post<UserSignUP>(
      REACT_APP_API_LOGIN + PATHS.RESEND_OTP,
      {
        username,
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const replenishmentUpload = async (file: File) => {
  try {
    const accessToken = getAccessToken();
    const formdata = new FormData();
    formdata.append("file", file, file.name);
    const response = await axios({
      method: "post",
      data: formdata,
      headers: {
        Authorization: accessToken,
      },
      url: REACT_APP_PROD_API_RIDGEWOOD + PATHS.REPLENISHMENT_UPLOAD,
    });

    return response.data;
  } catch (error) {
    //@ts-ignore
    return error.response.data;
  }
};

const downloadReplenishment = async (filters?: string[]) => {
  try {
    const accessToken = getAccessToken();
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };
    let dataToSend: any = {
      file_type: "xlsx",
    };

    if (filters && filters.length && filters[1].length) {
      let arr = filters[1]
        .toString()
        .split(",")
        .map((item: string) => item.trim().toString())
        .map((value) => {
          if (!isNaN(Number(value))) return Number(value);
          else return value;
        });

      dataToSend["filters_dict"] = { [filters[0]]: arr };
    }

    const data = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.REPLENISHMENT_DOWNLOAD,
      dataToSend,

      { headers }
    );
    return data.data;
  } catch (error) {
    throw error;
  }
};

const downloadAmazonPayment = async (filters?: any) => {
  try {
    const accessToken = getAccessToken();
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };
    let dataToSend: any = {
      file_type: "xlsx",
      ...filters,
    };

    const data = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.AMAZON_PAYMENT_DOWNLOAD,
      dataToSend,

      { headers }
    );
    return data.data;
  } catch (error) {
    throw error;
  }
};

const downloadAccounting = async (filters?: any) => {
  try {
    const accessToken = getAccessToken();
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };
    let dataToSend: any = {
      file_type: "xlsx",
      ...filters,
    };

    const data = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.ACCOUNTING_DOWNLOAD,
      dataToSend,

      { headers }
    );
    return data.data;
  } catch (error) {
    throw error;
  }
};

const downloadPnl = async (filters?: any) => {
  try {
    const accessToken = getAccessToken();
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };
    let dataToSend: any = {
      file_type: "xlsx",
      ...filters,
    };

    const data = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.PNL_DOWNLOAD,
      dataToSend,

      { headers }
    );
    return data.data;
  } catch (error) {
    throw error;
  }
};

const downloadLookup = async () => {
  try {
    const accessToken = getAccessToken();
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };

    const data = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.LOOKUP_DOWNLOAD,

      { headers }
    );
    return data.data;
  } catch (error) {
    throw error;
  }
};

const uploadLookup = async (file: File) => {
  try {
    const accessToken = getAccessToken();
    const formdata = new FormData();
    formdata.append("file", file, file.name);
    const { data } = await axios({
      method: "post",
      data: formdata,
      url: REACT_APP_PROD_API_RIDGEWOOD + PATHS.LOOKUP_UPLOAD,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return data;
  } catch (error) {
    throw error;
  }
};

const downloadInventory = async (filters?: any) => {
  try {
    const accessToken = getAccessToken();
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };
    let dataToSend: any = {
      file_type: "xlsx",
      ...filters,
    };

    const data = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.INVENTORY_DOWNLOAD,
      dataToSend,

      { headers }
    );
    return data.data;
  } catch (error) {
    throw error;
  }
};

const downloadErrorReportReplenishment = async (path: string) => {
  try {
    const accessToken = getAccessToken();
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    };

    const data = await axios.get<any>(REACT_APP_PROD_API_RIDGEWOOD + path, {
      headers: headers,
    });

    return data.data;
  } catch (error) {
    throw error;
  }
};

const downloadListingHealth = async (
  filters?: string[],
  orders_list?: string[]
) => {
  try {
    const accessToken = getAccessToken();
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };
    let dataToSend: any = {
      file_type: "xlsx",
    };

    if (filters && filters.length && filters[1].length) {
      let arr = filters[1]
        .toString()
        .split(",")
        .map((item: string) => item.trim())
        .map((value) => {
          if (!isNaN(Number(value))) return Number(value);
          else return value;
        });
      dataToSend["filters_dict"] = { [filters[0]]: arr };
    }
    if (orders_list && orders_list.length) {
      dataToSend["orders_list"] = [orders_list];
    }
    const data = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.LISTING_HEALTH_DOWNLOAD,
      dataToSend,

      { headers }
    );
    return data.data;
  } catch (error) {
    throw error;
  }
};

const downloadReport = async (report_id: string) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<string>(
      REACT_APP_DEV_RESTOCK + PATHS.DOWNLOAD_REPORT(report_id),
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getMismatchSummaryReport = async (filters?: string[]) => {
  try {
    const accessToken = getAccessToken();

    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: accessToken,
    };
    let dataToSend: any = {
      file_type: "xlsx",
    };

    if (filters && filters.length && filters[1].length) {
      let arr = filters[1]
        .toString()
        .split(",")
        .map((item: string) => item.trim())
        .map((value) => {
          if (!isNaN(Number(value))) return Number(value);
          else return value;
        });
      dataToSend["filters_dict"] = { [filters[0]]: arr };
    }

    const { data } = await axios.get<string>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.MISMATCH_SUMMARY_DOWNLOAD,
      { headers }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getShipmentData = async () => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get(
      REACT_APP_DEV_RESTOCK + PATHS.GET_SHIPMENT_DATA_DEV,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const shipmentActionChange = async (shipmentId: string, state: any) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post(
      REACT_APP_DEV_RESTOCK + PATHS.SHIPMENT_ACTION(shipmentId, state),
      null,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getForcastingData = async (
  // sellerIds: string[],
  orders_list?: string[],
  pageSize?: number,
  filters?: string[],
  page?: number
) => {
  try {
    const accessToken = getAccessToken();
    const dataToSend: any = {
      page,
      per_page: pageSize,
      // sellerIds: sellerIds,
    };
    if (filters && filters.length && filters[1].length) {
      let arr = filters[1]
        .toString()
        .split(",")
        .map((item: string) => item.trim())
        .map((value) => {
          if (!isNaN(Number(value))) return Number(value);
          else return value;
        });

      dataToSend["filters_dict"] = {
        [filters[0]]: arr,
      };
    }
    if (orders_list && orders_list.length && orders_list[0][0]) {
      dataToSend["orders_list"] = [orders_list];
    }

    const { data } = await axios.post(
      //REACT_APP_DEV_RESTOCK
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.GET_FORCASTING_DATA_DEV,
      { ...dataToSend },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getForcastingChartData = async (
  seller_ids: string[],
  top_asins: number = 20
) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD +
        PATHS.FORCASTING_CHART(seller_ids, top_asins),
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};
const getShipmentChartData = async (
  no_of_weeks: number,
  seller_ids: string[]
) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD +
        PATHS.SHIPMENT_CHART(no_of_weeks, seller_ids),
      null,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const downloadSellersDetails = async (
  no_of_weeks: number,
  seller_ids: string[]
) => {
  try {
    const accessToken = getAccessToken();
    let dataToSend: { file_type: string } = {
      file_type: "xlsx",
    };
    const { data } = await axios.post<string>(
      REACT_APP_PROD_API_RIDGEWOOD +
        PATHS.SELLERS_LIST(no_of_weeks, seller_ids),
      dataToSend,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getMismatchSummary = async (
  pageSize: number,
  page: number,
  orders_list?: [string[]] | [string[], string[]],
  filters?: string[],
  source?: string
) => {
  try {
    const accessToken = getAccessToken();
    const dataToSend: any = {
      page,
      per_page: pageSize,
      source_of_truth: source,
      offset: page === 1 || page === 0 ? 0 : pageSize * (page - 1),
    };
    if (filters && filters.length && filters[1].length) {
      let arr = filters[1]
        .toString()
        .split(",")
        .map((item: string) => item.trim())
        .map((value) => {
          if (!isNaN(Number(value))) return Number(value);
          else return value;
        });
      dataToSend["filters_dict"] = { [filters[0]]: arr };
    }
    if (orders_list && orders_list.length) {
      dataToSend["orders_list"] = orders_list;
    }
    const { data } = await axios.post<CatalogTrackerData>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.MISMATCH_SUMMARY,
      { ...dataToSend },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getCatalogAttributes = async (sellerId: string, asin: string) => {
  if (!asin || !sellerId) return;
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.CATALOG_ATTRIBUTES(asin, sellerId),
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const saveShipmentCaseId = async (shipmentId: string, caseId: string) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_DEV_RESTOCK + PATHS.SAVE_SHIPMENT_CASE_ID(shipmentId),
      { AmazonCaseId: caseId },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};
const forcastinUpload = async (file: File) => {
  try {
    const accessToken = getAccessToken();
    const formdata = new FormData();
    formdata.append("file", file, file.name);
    const { data } = await axios({
      method: "post",
      data: formdata,
      url: REACT_APP_PROD_API_RIDGEWOOD + PATHS.FORCASTING_UPLOAD,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return data;
  } catch (error) {
    throw error;
  }
};

const downloadShipmentCreationTextFile = async (
  reqObject: any,
  filetype: string
) => {
  try {
    const accessToken = getAccessToken();

    const { data } = await axios.post<File>(
      REACT_APP_DEV_RESTOCK +
        PATHS.DOWNLOAD_KEPLER_SHIPMENT_CREATION_TEXT(filetype),
      reqObject,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getForcastingEditData = async (
  // sellerIds: string[],
  orders_list?: string[],
  pageSize?: number,
  filters?: string[],
  page?: number,
  defaults?: boolean
) => {
  try {
    const accessToken = getAccessToken();
    const dataToSend: any = {
      page,
      per_page: pageSize,
      defaults: defaults ? defaults : false,
      // sellerIds: sellerIds,
    };
    if (filters && filters.length && filters[1].length) {
      let arr = filters[1]
        .toString()
        .split(",")
        .map((item: string) => item.trim())
        .map((value) => {
          if (!isNaN(Number(value))) return Number(value);
          else return value;
        });

      dataToSend["filters_dict"] = {
        [filters[0]]: arr,
      };
    }
    if (orders_list && orders_list.length && orders_list[0][0]) {
      dataToSend["orders_list"] = [orders_list];
    }
    const { data } = await axios.post<any>(
      // /REACT_APP_DEV_RESTOCK
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.FORCASTING_MANNUAL_OVERRIDE,
      { ...dataToSend },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const saveSnapshot = async (incomingData: any[]) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.TOTAL_SALES_SNAPSHOT,
      incomingData,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const getSnapshot = async () => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<ISnapshotApi>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.TOTAL_SALES_SNAPSHOT,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const saveForecastingData = async (
  job: boolean,
  Data: MagellanManualEditConfiguration[]
) => {
  try {
    const accessToken = getAccessToken();

    const { data } = await axios.put<MagellanManualEditConfiguration[]>(
      //@ts-ignore
      REACT_APP_PROD_API + PATHS.PUT_FORECASTING_DATA,

      Data,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getSalesChangesData = async () => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_DEV_RESTOCK + PATHS.SALES_CHANGES_DATA,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};
const getSalesChangesChartData = async (
  topAsins: number,
  expAvg: number,
  actAvg: number,
  sellerIds: string[] = [],
  toggleSwitchIndex: number
) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD +
        `${
          toggleSwitchIndex === 0
            ? PATHS.SALES_CHANGES_INCREASE_CHART
            : PATHS.SALES_CHANGES_DECREASE_CHART
        }`,
      {
        top_asins: topAsins,
        seller_ids: sellerIds.join(","),
        expected_average: expAvg,
        actual_average: actAvg,
      },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getSalesChangesTableData = async (sellerIds: string[] = []) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.SALES_CHANGES_TABLE_DATA,
      {
        seller_ids: sellerIds.join(","),
      },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getSalesDashboardData = async (
  aggregation_type: string,
  time_frames: string,
  seller: string
) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD +
        PATHS.SALES_DASHBOARD_DATA(aggregation_type, time_frames, seller),
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const getSalesBreakoutData = async (params: SalesBreakoutParams) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.SALES_BREAKOUT_DATA,
      { ...params },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getSignificantSalesChangesTableData = async (
  params: ISignificantSalesChangesParams
) => {
  try {
    const accessToken = getAccessToken();
    const apiUrl =
      `${REACT_APP_PROD_API_RIDGEWOOD}` +
      `${PATHS.SIGNIFICANT_SALES_CHANGES_TABLE}`;
    const { data } = await axios.post<SignificantSalesChangesTableResponse>(
      apiUrl,
      { ...params },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getSignificantSalesChangesChartData = async (
  params: ISignificantSalesChangesParams
) => {
  try {
    const accessToken = getAccessToken();
    const apiUrl =
      `${REACT_APP_PROD_API_RIDGEWOOD}` +
      `${PATHS.SIGNIFICANT_SALES_CHANGES_CHART}`;
    const { data } = await axios.post<SignificantSalesChangesChartResponse>(
      apiUrl,
      { ...params },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const saveConfig = async (configData: any) => {
  try {
    const accessToken = getAccessToken();

    const { data } = await axios({
      method: "post",
      url: REACT_APP_PROD_API_RIDGEWOOD + PATHS.SAVE_CONFIG,
      data: configData,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return data;
  } catch (error) {
    throw error;
  }
};

const getConfig = async () => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.GET_CONFIG,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const catalogTrackerUpload = async (file: File, selled_id?: string) => {
  try {
    const accessToken = getAccessToken();

    const formdata = new FormData();
    formdata.append("file", file, file.name);

    formdata.append("seller_id", selled_id ? selled_id : "");

    const { data } = await axios({
      method: "post",
      data: formdata,
      url: REACT_APP_PROD_API_RIDGEWOOD + PATHS.MISMATCH_SUMMARY_UPLOAD,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return data;
  } catch (error) {
    throw error;
  }
};

const calculateRangerData = async (calData: any) => {
  try {
    const accessToken = getAccessToken();

    const rangerData = {
      id: calData.id ? calData.id : 0,
      length: Number(calData.length),
      width: Number(calData.width),
      height: Number(calData.height),
      unit_of_dimension: calData.units_of_dimensions,
      weight: Number(calData.weight),
      unit_of_weight: calData.units_of_weight,
      lithium_battery: calData.lithium_battery ? calData.lithium_battery : "No",
      is_small_and_light: calData.is_small_and_light
        ? calData.is_small_and_light
        : "No",
      is_dangerous_goods: calData.is_dangerous_goods
        ? calData.is_dangerous_goods
        : "No",
    };

    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.CALCULATE_RANGER_DATA,
      _.isArray(calData) ? calData : [rangerData],
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};
const getSummaryChart = async (params: ISummaryChartParams) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<ISummaryChartData>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.SUMMARY_CHART(params),
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getInventoryPositionData = async (
  topAsins: string,
  seller_id: string
) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD +
        PATHS.INVENTORY_POSITION_DATA(topAsins, seller_id),
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getRankChangesData = async (
  aggregation: string,
  timeFrame: number,
  seller: string
) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.RANK_CHANGES_DATA,
      { seller_id: seller, weeks: timeFrame },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getRangerData = async (
  pageSize: number,
  page: number,
  orders_list?: string[],
  filters?: string[],
  selectedSellerIds?: string[]
) => {
  try {
    const accessToken = getAccessToken();
    const dataToSend: any = {
      page,
      per_page: pageSize,
      offset: page === 1 || page === 0 ? 0 : pageSize * (page - 1),
    };

    if (selectedSellerIds?.length) {
      dataToSend["filters_dict"] = {
        ["seller_id"]: selectedSellerIds,
      };
    }

    if (filters && filters.length && filters[1].length) {
      let arr = filters[1]
        .toString()
        .split(",")
        .map((item: string) => item.trim())
        .map((value) => {
          if (!isNaN(Number(value))) return Number(value);
          else return value;
        });

      dataToSend["filters_dict"] = {
        ...dataToSend["filters_dict"],
        [filters[0]]: arr,
      };
    }

    if (orders_list && orders_list.length) {
      dataToSend["orders_list"] = [orders_list];
    }

    const { data } = await axios.post<any[]>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.GET_RANGER_DATA,
      { ...dataToSend },

      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};
const getAdvertisingData = async (
  aggregation_type: string,
  grouping_type: string,
  time_frames: number,
  seller_id: string,
  top_x: string
) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD +
        PATHS.ADVERTISING_DATA(
          seller_id,
          aggregation_type,
          grouping_type,
          time_frames,
          top_x
        ),
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const downloadAdvertisingData = async (
  seller_id: string,
  aggregation_type: string,
  grouping_type: string,
  time_frames: string
) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<string>(
      REACT_APP_PROD_API_RIDGEWOOD +
        PATHS.ADVERTISING_LIST(
          seller_id,
          aggregation_type,
          grouping_type,
          time_frames
        ),
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getSearchRank = async (seller_id: string) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.SEARCH_RANK(seller_id),
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const saveSearchRank = async (params: IRankChangesSave[]) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<IRankChangesSaveApi>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.ADD_SEARCH_TERM,
      { data: params },

      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const getAmazonPayment = async (params: any) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.AMAZON_PAYMENT,
      { ...params },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const getAccountingTableData = async (params: any) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<AccountingTableDataResponse>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.ACCOUNTING_TABLE,
      { ...params },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const getInventory = async (params: any) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.INVENTORY,
      { ...params },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const getPnlView = async (params: any) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.PNL_VIEW,
      { ...params },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const rankChangesSave = async (params: any) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.RANK_CHANGES_ADD,
      { ...params },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const getPnlEdit = async (seller: string) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<IPnlEdit>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.PNL_EDIT(seller),
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const pnlEditSave = async (params: IPnlEditItem[]) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.put<IPnlEditItem[]>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.PNL_EDIT_SAVE,
      params,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const pnlEditUpload = async (file: File) => {
  try {
    const accessToken = getAccessToken();
    const formdata = new FormData();
    formdata.append("file", file, file.name);
    const { data } = await axios({
      method: "post",
      data: formdata,
      url: REACT_APP_PROD_API_RIDGEWOOD + PATHS.PNL_EDIT_UPLOAD,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return data;
  } catch (error) {
    throw error;
  }
};

const getPnlEditDownload = async (seller: string) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.PNL_EDIT_DOWNLOAD(seller),
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const savePnlView = async (params: any) => {
  try {
    const obj = {
      obj: params,
    };
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.PNL_SUBMIT,
      { ...params },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const getPnlPaymentView = async (params: any) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<IPNLApi>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.PNL_PAYMENT_VIEW,
      { ...params },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const getPaymentLookup = async () => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.LOOKUP,

      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const getSubscriptionDetails = async (token: string) => {
  try {
    const accessToken = getAccessToken();
    //Need to investigate to replace with axios
    let data = await fetch(REACT_APP_SUBSCRIPTION + PATHS.SUBSCRIPTION_DETAILS, {
      method: "GET",
      headers: {
        Authorization: accessToken ? accessToken : token,
      },
    })
      .then(async (res) => {
          const data = await res.json();
          return { status: res.status, data };
      })
      .then((response) => response);
    return data;
  } catch (error) {
    throw error;
  }
};

const stripeCheckout = async () => {
  try {
    const accessToken = getAccessToken();
    //Need to investigate to replace with axios
    let data = await fetch(REACT_APP_SUBSCRIPTION + PATHS.CUSTOMER_PORTAL, {
      method: "GET",
      headers: {
        Authorization: accessToken,
      },
    })
      .then((res) => {
        if (res.ok) {
          return res.json();
        }
      })
      .then((response) => response);
    return data;
  } catch (error) {
    throw error;
  }
};

const productSubscription = async (productType: {product_type: string}) =>
{
  try {
    const accessToken = getAccessToken();
    //Need to investigate to replace with axios
    let data = await fetch(REACT_APP_SUBSCRIPTION + PATHS.PRODUCT_CHECKOUT, {
      method: "POST",
      body: JSON.stringify({...productType}),
      headers: {
        Authorization: accessToken,
      },
    })
      .then((res) => {
          return res.json();
      })
      .then((response) => response);
    return data;
  } catch (error) {
    throw error;
  }
};

const checkPaymentStatus = async (session_id: string) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      //REACT_APP_SUBSCRIPTION
      REACT_APP_SUBSCRIPTION + PATHS.PAYMENT_STATUS(session_id),

      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const getPnlChart = async (params: any) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.PNL_PAYMENT_VIEW_CHART,
      { ...params },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const uploadRangerData = async (file: File) => {
  try {
    const accessToken = getAccessToken();
    const formdata = new FormData();
    formdata.append("file", file, file.name);
    const { data } = await axios({
      method: "post",
      data: formdata,
      url: REACT_APP_PROD_API_RIDGEWOOD + PATHS.RANGER_UPLOAD,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return data;
  } catch (error) {
    throw error;
  }
};
const downloadCalculatorTemplate = async () => {
  try {
    const accessToken = getAccessToken();
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };

    const data = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.CALCULATOR_TEMPLATE_DOWNLOAD,
      { headers }
    );
    return data.data;
  } catch (error) {
    throw error;
  }
};
const getRangerFBAMeasurementData = async (
  pageSize: number,
  page: number,
  orders_list?: string[],
  filters?: string[],
  selectedSellerIds?: string[]
) => {
  try {
    const accessToken = getAccessToken();
    const dataToSend: any = {
      page,
      per_page: pageSize,
      offset: page === 1 || page === 0 ? 0 : pageSize * (page - 1),
    };

    if (selectedSellerIds?.length) {
      dataToSend["filters_dict"] = {
        ["seller_id"]: selectedSellerIds,
      };
    }

    if (filters && filters.length && filters[1].length) {
      let arr = filters[1]
        .toString()
        .split(",")
        .map((item: string) => item.trim())
        .map((value) => {
          if (!isNaN(Number(value))) return Number(value);
          else return value;
        });

      dataToSend["filters_dict"] = {
        ...dataToSend["filters_dict"],
        [filters[0]]: arr,
      };
    }

    if (orders_list && orders_list.length) {
      dataToSend["orders_list"] = [orders_list];
    }

    const { data } = await axios.post<any[]>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.FBA_MEASUREMENT,
      { ...dataToSend },

      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const dimensionImprovementUpload = async (file: File) => {
  try {
    const accessToken = getAccessToken();
    const formdata = new FormData();
    formdata.append("file", file, file.name);
    const { data } = await axios({
      method: "post",
      data: formdata,
      url: REACT_APP_PROD_API_RIDGEWOOD + PATHS.DIMENSION_IMPROVEMENT_UPLOAD,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return data;
  } catch (error) {
    throw error;
  }
};
const FBAMeasurementUpload = async (file: File) => {
  try {
    const accessToken = getAccessToken();
    const formdata = new FormData();
    formdata.append("file", file, file.name);
    const { data } = await axios({
      method: "post",
      data: formdata,
      url: REACT_APP_PROD_API_RIDGEWOOD + PATHS.FBA_MEASUREMENT_UPLOAD,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return data;
  } catch (error) {
    throw error;
  }
};
const createNewSeller = async (newSeller: any) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_DEV_RESTOCK + PATHS.CREATE_SELLERS,
      newSeller,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};
const createCompanyUser = async (newUser: any) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any[]>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.CREATE_OR_GET_ALL_USERS,
      newUser,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getCompanyById = async (companyId: string) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_DEV_RESTOCK + PATHS.GET_OR_MODIFY_COMPANY(companyId),
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const modifyUserRole = async (userId: string, user: UserModifyData) => {
  try {
    const accessToken = getAccessToken();
    const status = await axios.put<any, number[]>(
      REACT_APP_DEV_RESTOCK + PATHS.MODIFY_USER_ROLE(userId),
      user,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return status;
  } catch (error) {
    throw error;
  }
};
const deleteUser = async (username: string) => {
  try {
    const accessToken = getAccessToken();
    const status = await axios.delete<unknown, number[]>(
      REACT_APP_PROD_API + PATHS.GET_SPECIFIC_USER,
      {
        headers: {
          Authorization: accessToken,
        },
        data: {
          username
        }
      }
    );
    return status;
  } catch (error) {
    throw error;
  }
};

const getSellerStatus = async () => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.GET_SELLER_STATUS,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getDashboardData = async () => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.GET_DASHBOARD_DATA,

      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getDashboardDemoData = async () => {
  try {
    const { data } = await axios.get<any>(
      DEMO_API + PATHS.GET_DASHBOARD_DATA,

      {
        headers: {
          Authorization: DASHBOARD_DEMO_DATA_TOKEN,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const changePassword = async (
  username: string,
  temp_password: string,
  confirm_password: string
) => {
  try {
    const data = await axios.post<any>(
      REACT_APP_API_LOGIN + PATHS.CHANGE_PASSWORD,
      {
        username,
        temp_password,
        confirm_password,
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};
const savePaymentLookup = async (params: any[]) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios({
      method: "put",
      url: REACT_APP_PROD_API_RIDGEWOOD + PATHS.LOOKUP,
      data: params,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return data;
  } catch (error) {
    throw error;
  }
};

const downloadForecastingData = async (
  filters?: string[],
  orders_list?: string[]
) => {
  try {
    const accessToken = getAccessToken();
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };
    let dataToSend: any = {
      // file_type: "xlsx",
    };

    if (filters && filters.length && filters[1].length) {
      let arr = filters[1]
        .toString()
        .split(",")
        .map((item: string) => item.trim())
        .map((value) => {
          if (!isNaN(Number(value))) return Number(value);
          else return value;
        });
      dataToSend["filters_dict"] = { [filters[0]]: arr };
    }

    if (orders_list && orders_list.length) {
      dataToSend["orders_list"] = [orders_list];
    }

    const data = await axios.post<any>(
      REACT_APP_PROD_API + PATHS.FORECASTING_DOWNLOAD,
      dataToSend,

      { headers }
    );
    return data.data;
  } catch (error) {
    throw error;
  }
};

const downloadForecastingEditData = async (
  defaults: boolean,
  filters?: string[],
  orders_list?: string[]
) => {
  try {
    const accessToken = getAccessToken();
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };
    let dataToSend: any = {
      defaults,
    };

    if (filters && filters.length && filters[1].length) {
      let arr = filters[1]
        .toString()
        .split(",")
        .map((item: string) => item.trim())
        .map((value) => {
          if (!isNaN(Number(value))) return Number(value);
          else return value;
        });
      dataToSend["filters_dict"] = { [filters[0]]: arr };
    }

    if (orders_list && orders_list.length) {
      dataToSend["orders_list"] = [orders_list];
    }

    const data = await axios.post<any>(
      REACT_APP_PROD_API + PATHS.FORECASTING_EDIT_DATA_DOWNLOAD,
      dataToSend,

      { headers }
    );
    return data.data;
  } catch (error) {
    throw error;
  }
};
const activeOrInactivateSeller = async (
  sellerId: string,
  is_active: number
) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.ACTIVE_OR_INACTIVE_SELLER,
      {
        seller_id: sellerId,
        is_active,
      },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const userHasSellerOrNot = async () => {
  try {
    const accessToken = getAccessToken();

    const { data } = await axios.get<UserHasSellerOrNotResponseParams>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.USER_HAS_SELLER_NOT,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};
const postCuriosityUpvote = async (body: PostCuriosityUpvote) => {
  try {
    const { data } = await axios.post<string | Error>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.CURIOSITY_UPVOTE,
      {
        widget_id: body.widget_id,
        username: body.username,
        email: body.email,
        vote: body.vote,
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const postCuriosityFeedback = async (body: PostCuriosityFeedback) => {
  try {
    const { data } = await axios.post<string | Error>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.CURIOSITY_FEEDBACK,
      {
        content: body.content,
        username: body.username,
        email: body.email,
        attach_file: body.attach_file,
      },
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const verifyUsername = async (username: string) => {
  try {
    const data = await axios.post<ForgotPasswordProps>(
      REACT_APP_API_LOGIN + PATHS.VERIFY_USERNAME,
      {
        username,
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};
const forgotPassword = async (
  username: string,
  confirmation_code: string,
  new_password: string
) => {
  try {
    const data = await axios.post<ForgotPasswordProps>(
      REACT_APP_API_LOGIN + PATHS.FORGOT_PASSWORD,
      {
        username,
        confirmation_code,
        new_password,
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getCuriosityUpvote = async () => {
  try {
    const { data } = await axios.get<CuriosityUpvoteProps>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.CURIOSITY_UPVOTE,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};
const saveShipmentNotes = async (shipmentId: string, Notes: string) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.post<number[]>(
      REACT_APP_DEV_RESTOCK + PATHS.SAVE_SHIPMENT_CASE_ID(shipmentId),
      { Notes },
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const getAdvertisingChartData = async (
  aggregation_type: string,
  grouping_type: string,
  time_frames: number,
  seller_id: string,
  top_x: string
) => {
  try {
    const accessToken = getAccessToken();
    const { data } = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD +
        PATHS.ADVERTISING_CHART(
          seller_id,
          aggregation_type,
          grouping_type,
          time_frames,
          top_x
        ),
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );

    return data;
  } catch (error) {
    throw error;
  }
};

const saveRangerSnapshot = async (calData: any) => {
  try {
    const accessToken = getAccessToken();

    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.RANGER_SNAPSHOT,
      calData,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const downloadAmazonTemplate = async () => {
  try {
    const accessToken = getAccessToken();
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };

    const data = await axios.get<any>(
      REACT_APP_PROD_API_RIDGEWOOD +
        PATHS.AMAZON_FBA_FEES_RATED_TEMPLATE_DOWNLOAD,
      { headers }
    );
    return data.data;
  } catch (error) {
    throw error;
  }
};

const amazonRateTemplateUpload = async (file: File) => {
  try {
    const accessToken = getAccessToken();
    const formdata = new FormData();
    formdata.append("file", file, file.name);
    const { data } = await axios({
      method: "post",
      data: formdata,
      url:
        REACT_APP_PROD_API_RIDGEWOOD +
        PATHS.AMAZON_FBA_FEES_RATED_TEMPLATE_UPLOAD,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return data;
  } catch (error) {
    throw error;
  }
};

const calculateDimensionImprovementData = async (calData: any) => {
  try {
    const accessToken = getAccessToken();

    const { data } = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.CALCULATE_DIMENSION_IMPROVEMENT,
      calData,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return data;
  } catch (error) {
    throw error;
  }
};

const modifyUser = async (user: UserModifyData) => {
  try {
    const accessToken = getAccessToken();
    const status = await axios.put<any, number[]>(
      REACT_APP_PROD_API + PATHS.GET_SPECIFIC_USER,
      user,
      {
        headers: {
          Authorization: accessToken,
        },
      }
    );
    return status;
  } catch (error) {
    throw error;
  }
};

const downloadDimensionImprovement = async (
  filters?: string[],
  orders_list?: string[]
) => {
  try {
    const accessToken = getAccessToken();
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };
    let dataToSend: any = {
      file_type: "xlsx",
    };

    if (orders_list && orders_list.length) {
      dataToSend["orders_list"] = [orders_list];
    }
    if (filters && filters.length && filters[1].length) {
      let arr = filters[1]
        .toString()
        .split(",")
        .map((item: string) => item.trim().toString())
        .map((value) => {
          if (!isNaN(Number(value))) return Number(value);
          else return value;
        });

      dataToSend["filters_dict"] = { [filters[0]]: arr };
    }

    const data = await axios.post<any>(
      REACT_APP_PROD_API_RIDGEWOOD + PATHS.DIMENSION_IMPROVEMENT_DOWNLOAD,
      dataToSend,

      { headers }
    );
    return data.data;
  } catch (error) {
    throw error;
  }
};

export const apiClient = Object.freeze({
  getReplenishmentData,
  getPromotionCalendarData,
  getPromotionCalendarTableData,
  getUserDetails,
  getCompanyAnalytics,
  getUserAnalytics,
  saveReplenishmentData,
  getReplenishmentCalculationStatus,
  userLogin,
  userSignup,
  userSignupVerify,
  resendOTP,
  downloadReplenishment,
  downloadErrorReportReplenishment,
  getShipmentData,
  replenishmentUpload,
  getReplenishmentChartData,
  getSalesReportsData,
  downloadReport,
  getSellerLimitsData,
  shipmentActionChange,
  updateApolloShipmentDisputeState,
  getForcastingData,
  getShipmentChartData,
  downloadSellersDetails,
  getListingHealth,
  downloadListingHealth,
  getListingHealthChartData,
  getForcastingChartData,
  saveShipmentCaseId,
  forcastinUpload,
  downloadShipmentCreationTextFile,
  getForcastingEditData,
  saveForecastingData,
  getMismatchSummary,
  getMismatchSummaryReport,
  getCatalogAttributes,
  catalogTrackerUpload,
  getSalesChangesData,
  getSalesChangesChartData,
  calculateRangerData,
  getRangerData,
  uploadRangerData,
  downloadCalculatorTemplate,
  getSalesDashboardData,
  getSalesBreakoutData,
  getInventoryPositionData,
  getRankChangesData,
  getAdvertisingData,
  downloadAdvertisingData,
  getShipmentStatus,
  getSummaryChart,
  saveSnapshot,
  getSignificantSalesChangesChartData,
  getSignificantSalesChangesTableData,
  getRangerFBAMeasurementData,
  dimensionImprovementUpload,
  FBAMeasurementUpload,
  saveConfig,
  getConfig,
  getSearchRank,
  saveSearchRank,
  getAmazonPayment,
  downloadAmazonPayment,
  getAccountingTableData,
  downloadAccounting,
  getInventory,
  getPnlView,
  getPnlPaymentView,
  getPnlChart,
  downloadInventory,
  createNewSeller,
  createCompanyUser,
  getCompanyById,
  modifyUserRole,
  deleteUser,
  getSellerStatus,
  downloadPnl,
  getPaymentLookup,
  getDashboardData,
  downloadLookup,
  uploadLookup,
  savePnlView,
  changePassword,
  savePaymentLookup,
  getSubscriptionDetails,
  stripeCheckout,
  productSubscription,
  checkPaymentStatus,
  getDashboardDemoData,
  downloadForecastingData,
  downloadForecastingEditData,
  getSalesChangesTableData,
  activeOrInactivateSeller,
  userHasSellerOrNot,
  getPnlEdit,
  getPnlEditDownload,
  pnlEditSave,
  pnlEditUpload,
  postCuriosityUpvote,
  getSnapshot,
  postCuriosityFeedback,
  verifyUsername,
  forgotPassword,
  getCuriosityUpvote,
  saveShipmentNotes,
  rankChangesSave,
  getAdvertisingChartData,
  saveRangerSnapshot,
  downloadAmazonTemplate,
  amazonRateTemplateUpload,
  calculateDimensionImprovementData,
  modifyUser,
  downloadDimensionImprovement,
});
